import * as ImagePicker from "expo-image-picker";
import { Star } from "lucide-react-native";
import { FC, useState } from "react";

import {
  DropdownPicker,
  HStack,
  MediaPicker,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import { Question, QuestionDraft, Rule } from "@/types/resources";
import { QuestionSuggestion } from "@/types/responses";
import {
  RulesEditor,
  conditionSourceQuestions,
  hasApplicableRules,
} from "../rules";
import AdvancedSettings from "./AdvancedSettings";
import QuestionField from "./QuestionField";
import QuestionTextInput from "./QuestionTextInput";

const MAX_MEDIA = 10;
const DEFAULT_SCALE = 5;
const MIN_SCALE = 3;
const MAX_SCALE = 10;

interface RatingScaleQuestionEditorProps {
  allQuestions?: Question[];
  currentQuestionOrder?: number;
  disabled?: boolean;
  onChange: (question: QuestionDraft<"rating_scale">) => void;
  question: QuestionDraft<"rating_scale">;
}

/**
 * Rating scale options format:
 * options = ["1", "2", "3", "4", "5"] for a 5-star rating
 * The scale size is determined by options.length
 */
const getScaleSize = (options: string[]): number | null => {
  const size = options.length;
  if (size < MIN_SCALE) return null;
  return size <= MAX_SCALE ? size : DEFAULT_SCALE;
};

/**
 * Generate options array for a given scale size
 * e.g., generateOptions(5) returns ["1", "2", "3", "4", "5"]
 */
const generateOptions = (size: number): string[] => {
  return Array.from({ length: size }, (_, i) => String(i + 1));
};

const RatingScaleQuestionEditor: FC<RatingScaleQuestionEditorProps> = ({
  allQuestions = [],
  currentQuestionOrder,
  disabled = false,
  onChange,
  question,
}) => {
  const { i18n } = useLocalization();
  const [isSuggesting, setIsSuggesting] = useState(false);
  const isDisabled = disabled || isSuggesting;

  const scaleSize = getScaleSize(question.options);
  const rules = question.rules || [];

  const previousConditionQuestions = conditionSourceQuestions(
    allQuestions,
    currentQuestionOrder
  );

  const handleQuestionTextChange = (text: string) => {
    onChange({ ...question, text });
  };

  const handleSuggest = (suggestion: QuestionSuggestion) => {
    onChange({ ...question, text: suggestion.text });
  };

  const handleScaleSizeChange = (value: string) => {
    const size = parseInt(value, 10);
    onChange({
      ...question,
      options: generateOptions(size),
    });
  };

  const handleMediaChange = (
    newMedia: (ImagePicker.ImagePickerAsset | string)[]
  ) => {
    onChange({ ...question, media: newMedia });
  };

  const handleRulesChange = (newRules: Rule[]) => {
    onChange({ ...question, rules: newRules });
  };

  // Generate scale dropdown options (3-10)
  const scaleOptions = [];
  for (let i = MIN_SCALE; i <= MAX_SCALE; i++) {
    scaleOptions.push({ label: String(i), value: String(i) });
  }

  return (
    <MediaPicker
      isDisabled={isDisabled}
      maxItems={MAX_MEDIA}
      media={question.media}
      onMediaChange={handleMediaChange}
    >
      <VStack className="gap-6">
        <VStack className="gap-3">
          <QuestionField order={currentQuestionOrder}>
            <QuestionTextInput
              disabled={disabled}
              lines={question.lines}
              onChange={handleQuestionTextChange}
              onLoadingChange={setIsSuggesting}
              onSuggest={handleSuggest}
              options={question.options}
              text={question.text || ""}
              type={question.type}
            />
            <MediaPicker.Trigger compact height={70} width={70} />
          </QuestionField>
          <MediaPicker.Thumbnails />
          <MediaPicker.Error />
        </VStack>

        {/* Scale */}
        <VStack className="gap-3">
          <HStack className="items-center gap-1">
            <Text className="text-sm font-semibold text-typography-800">
              {i18n.t("questions.scaleLabel")}
            </Text>
            <Text className="text-sm font-semibold text-error-500">*</Text>
          </HStack>
          <HStack className="items-center gap-2">
            <DropdownPicker
              className="w-24"
              isDisabled={isDisabled}
              items={scaleOptions}
              onChange={handleScaleSizeChange}
              placeholder={i18n.t("questions.selectScale")}
              selectKey={`rating-scale-${scaleSize}`}
              value={scaleSize !== null ? String(scaleSize) : null}
            />
            <Star className="text-outline-400" size={18} />
          </HStack>
        </VStack>

        {/* Skip logic */}
        {hasApplicableRules(allQuestions, currentQuestionOrder) && (
          <AdvancedSettings defaultExpanded={rules.length > 0}>
            <RulesEditor
              allQuestions={allQuestions}
              currentQuestionOrder={currentQuestionOrder}
              disabled={isDisabled}
              onRulesChange={handleRulesChange}
              previousConditionQuestions={previousConditionQuestions}
              rules={rules}
            />
          </AdvancedSettings>
        )}
      </VStack>
    </MediaPicker>
  );
};

export default RatingScaleQuestionEditor;
